import { CategoryEntity, GetDocumentsByAssetRequestDTO, GetDocumentsRequestDTO, RecordState } from '@gearbelt/global';
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { ApiService } from '@/services/api-v2';
import { AWAApiV2Service } from '@/services/awa-api-v2';

export const documentKeys = {
  all: ['documents'] as const,
  infinite: (filters?: GetDocumentsRequestDTO['filters'], orderBy?: GetDocumentsRequestDTO['orderBy']) =>
    [...documentKeys.all, 'infinite', filters, orderBy] as const,
  byAsset: (assetId: string) => [...documentKeys.all, 'by-asset', assetId] as const,
};

export const documentCategoryKeys = {
  all: ['document-categories'] as const,
  list: (orgId: string | undefined) => [...documentCategoryKeys.all, orgId] as const,
};

export const getDocumentCategoriesQueryOptions = ({ orgId }: { orgId: string | undefined }) => {
  return queryOptions({
    queryKey: documentCategoryKeys.list(orgId),
    queryFn: async () =>
      (
        await (
          await ApiService.GetCategories({
            filters: {
              fields: { entity: CategoryEntity.Document },
            },
          })
        ).json()
      ).data,
  });
};

export const assetDocumentKeys = {
  all: ['asset-documents'] as const,
  infinite: (assetId: string, filters?: Omit<GetDocumentsByAssetRequestDTO['filters'], 'assetId'>, orderBy?: GetDocumentsByAssetRequestDTO['orderBy']) =>
    [...assetDocumentKeys.all, 'infinite', assetId, filters, orderBy] as const,
};

export const getDocumentsInfiniteQueryOptions = ({
  filters,
  orderBy,
  enabled = true,
}: {
  filters?: GetDocumentsRequestDTO['filters'];
  orderBy?: GetDocumentsRequestDTO['orderBy'];
  enabled?: boolean;
}) => {
  return infiniteQueryOptions({
    queryKey: documentKeys.infinite(filters, orderBy),
    queryFn: async ({ pageParam }: { pageParam?: string }) => await (await ApiService.GetDocuments({ limit: 30, cursors: pageParam, filters, orderBy })).json(),
    getNextPageParam: (lastPage) => (lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined),
    initialPageParam: undefined as string | undefined,
    retry: false,
    gcTime: 0,
    enabled,
  });
};

export const getDocumentsByAssetInfiniteQueryOptions = ({
  assetId,
  filters,
  orderBy,
  enabled = true,
}: {
  assetId: string;
  filters?: Omit<GetDocumentsByAssetRequestDTO['filters'], 'assetId'>;
  orderBy?: GetDocumentsByAssetRequestDTO['orderBy'];
  enabled?: boolean;
}) => {
  return infiniteQueryOptions({
    queryKey: assetDocumentKeys.infinite(assetId, filters, orderBy),
    queryFn: async ({ pageParam }: { pageParam?: string }) =>
      await (await ApiService.GetDocumentsByAsset({ assetId, limit: 30, cursors: pageParam, filters, orderBy })).json(),
    getNextPageParam: (lastPage) => (lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined),
    initialPageParam: undefined as string | undefined,
    retry: false,
    gcTime: 0,
    enabled,
  });
};

export const getAWADocumentsByAssetQueryOptions = ({ assetId }: { assetId: string }) => {
  return queryOptions({
    queryKey: documentKeys.byAsset(assetId),
    queryFn: async () =>
      await (
        await AWAApiV2Service.GetDocumentsByAsset({
          assetId,
          filters: { state: RecordState.Active },
          orderBy: { direction: 'desc', field: 'createdAt' },
        })
      ).json(),
    enabled: !!assetId,
  });
};
